<template>
  <div class="whiteBg p-p-5">
    <h1 class="title is-2">
      {{
        $t("unscheduled", {
          type: schedule.is_practice ? $t("practices") : $t("games"),
          count: schedule.unscheduled_items.length,
        })
      }}
    </h1>
    <div class="p-grid p-mt-3">
      <div class="p-col-4 title is-4">{{ schedule.is_practice ? $t('GameTable.column.team') : $t('GameTable.column.visitor') }}</div>
      <div v-if="!schedule.is_practice" class="p-col-4 title is-4">{{ $t("GameTable.column.home") }}</div>
      <div class="p-col-4"></div>
    </div>
    <div v-for="i in schedule.unscheduled_items" :key="i.id" class="p-grid">
      <div class="p-col-4">{{ i.game.visitor.name }}</div>
      <div v-if="!schedule.is_practice" class="p-col-4">{{ i.game.home.name }}</div>
      <div class="p-col-4">
        <Button :label="$t('choose-timeslot')" @click="startChoose(i)" />
        &nbsp;
        <Button class="p-button-text" @click="deleteGame(i)" :label="$t('delete-game')" />
      </div>
    </div>

    <Dialog
      :show-header="false"
      :visible.sync="chooseDialog"
      :style="{ width: '70%', minWidth: '1000px', maxWidth: '90vw' }"
      :modal="true"
    >
      <div v-if="creating && activeItem">
        <div class="title is-4 p-mt-5">
          {{
            $t("creating_timeslot", { visitorName: activeItem.game.visitor.name, homeName: activeItem.game.home.name })
          }}
          <Button
            :label="$t('Button.cancel')"
            @click="creating = false"
            class="p-button-small p-button-text floatRight"
          />
        </div>
        <div class="p-grid p-mt-5" style="max-width: 800px; margin: 0 auto">
          <div class="p-col-6">
            <div class="title is-6">{{ $t("start-date-time") }}</div>
            <datetime
              :use12-hour="true"
              class="w100"
              type="datetime"
              @input="setEnd"
              v-model="timeslot.start"
            ></datetime>
          </div>
          <div class="p-col-6">
            <div class="title is-6">{{ $t("end-date-time") }}</div>
            <datetime
              :use12-hour="true"
              class="w100"
              v-if="timeslot.start"
              @input="checkEnd"
              type="datetime"
              v-model="timeslot.finish"
            ></datetime>
            <p v-else>{{ $t("enter-start-first") }}</p>
          </div>
          <div class="p-col-6">
            <div class="title is-6">{{ $t("GameTable.column.venue") }}</div>
            <Dropdown
              class="w100"
              :options="seasonItems.venues"
              optionLabel="name"
              optionValue="id"
              :placeholder="$t('select-venue')"
              v-model="timeslot.venue_id"
            />
          </div>
          <div class="p-col-6">
            <div class="title is-6">{{ $t("GameTable.column.surface") }}</div>
            <Dropdown
              class="w100"
              v-if="timeslot.venue_id"
              :options="seasonItems.surfaces"
              optionLabel="name"
              optionValue="id"
              :placeholder="$t('select-surface')"
              v-model="timeslot.surface_id"
            />
            <p v-else>{{ $t("select-venue-first") }}</p>
          </div>
          <div class="p-col-12 p-mt-5 p-text-center">
            <Button :label="$t('Button.save')" :disabled="!valid" @click="saveTimeslot" class="p-button-lg" />
          </div>
        </div>
      </div>

      <div v-else-if="activeItem" class="">
        <div class="p-field p-col-12">
          <h2 class="title is-3 p-mt-3">
            <span v-if="schedule.is_practice">
              {{ $t("assign-activeitem-game-home-name-practice", [activeItem.game.home.name]) }}
            </span>
            <span v-else>{{
              $t("assign-game-activeitem-game-visitor-name-at-activeitem-game-home-name", [
                activeItem.game.visitor.name,
                activeItem.game.home.name,
              ])
            }}</span>
            <Button
              :label="$t('Button.cancel')"
              @click="chooseDialog = false"
              class="p-button-small p-button-text floatRight"
            />
          </h2>
        </div>
        <div class="title is-4 p-mt-3">
          {{ eligibleTimeslots.length ? $t('choose-timeslot-or') : $t('no-available-timeslots-for-this-pairing') }}
          <a @click="creating = true">{{ $t("create-new-timeslot") }}</a>
          <br />
          <div v-if="schedule.balances" class="p-mt-3">
            <Button
              :label="$t('team-balance')"
              @click="chooseBalance = 'team_balance'"
              class="p-button-small p-button-text"
            />
            <Button
              :label="$t('venue-balance')"
              @click="chooseBalance = 'venue_balance'"
              class="p-button-small p-button-text"
            />
            <Button
              :label="$t('travel-balance')"
              @click="chooseBalance = 'travel_balance'"
              class="p-button-small p-button-text"
            />
            <Button
              :label="$t('team-week-balance')"
              @click="chooseBalance = 'team_week_balance'"
              class="p-button-small p-button-text"
            />
            <br />
            <Button
              :label="$t('days-of-week-balance')"
              @click="chooseBalance = 'days_balance'"
              class="p-button-small p-button-text"
            />
            <Button
              :label="$t('rest-teams-per-week')"
              @click="chooseBalance = 'rest_per_week'"
              class="p-button-small p-button-text"
            />
          </div>
        </div>
        <div
          v-if="schedule.balances && !schedule.is_practice && chooseBalance == 'team_balance'"
          class="p-mt-3"
          id="teamBalance"
        >
          <h5 class="title is-4 p-mt-3" style="text-align: center; margin-top: 20px">
            {{ $t("team-balance") }}
          </h5>
          <DataTable
            :value="schedule.balances.team_balance"
            id="teamsTable"
            ref="teamsTable"
            class="p-datatable-sm p-datatable-striped"
          >
            <Column
              v-for="(b, i) in schedule.balances.team_names"
              :header="schedule.balances.team_names[i]"
              :field="schedule.balances.team_names[i]"
              :key="i"
            ></Column>
          </DataTable>
        </div>
        <div
          v-if="schedule.balances && !schedule.is_practice && chooseBalance == 'venue_balance'"
          class="p-mt-3"
          id="venueBalance"
        >
          <h5 class="title is-4 p-mt-3" style="text-align: center; margin-top: 20px">
            {{ $t("venue-balance") }}
          </h5>
          <DataTable
            id="venuesTable"
            ref="venuesTable"
            :value="schedule.balances.venue_balance"
            class="p-datatable-sm p-datatable-striped"
          >
            <Column
              v-for="(b, i) in schedule.balances.venue_names"
              :header="schedule.balances.venue_names[i]"
              :field="schedule.balances.venue_names[i]"
              sortable
              :key="i"
            ></Column>
          </DataTable>
        </div>
        <div v-if="schedule.balances && chooseBalance == 'travel_balance'" class="p-mt-3" id="travelBalance">
          <h5 class="title is-4 p-mt-3" style="text-align: center; margin-top: 20px">
            <span v-if="isPrinting && !isAll"> {{ $t("schedule-schedule-name", [schedule.name]) }} <br /><br /></span>
            <span v-if="!schedule.is_practice">{{ $t("home-away-and") }} </span> {{ $t("early-late-balance") }}
          </h5>
          <DataTable
            id="timesTable"
            ref="timesTable"
            :value="schedule.balances.travel_balance"
            class="p-datatable-sm p-datatable-striped"
          >
            <Column
              v-for="(b, i) in schedule.balances.travel_names"
              :header="schedule.balances.travel_names[i]"
              :field="schedule.balances.travel_names[i]"
              :key="i"
            ></Column>
          </DataTable>
          <br />
        </div>
        <div v-if="schedule.balances && chooseBalance == 'team_week_balance'" class="p-mt-3" id="teamWeekBalance">
          <h5 class="title is-4 p-mt-3" style="text-align: center; margin-top: 20px">
            {{ $t("team-week-balance") }}
          </h5>
          <DataTable
            id="weekTable"
            ref="weekTable"
            :value="schedule.balances.week_balance"
            class="p-datatable-sm p-datatable-striped"
          >
            <Column
              v-for="(b, i) in schedule.balances.week_names"
              :header="schedule.balances.week_names[i]"
              :field="schedule.balances.week_names[i]"
              :key="i"
            ></Column>
          </DataTable>
        </div>
        <div v-if="schedule.balances && chooseBalance == 'days_balance'" class="p-mt-3" id="daysTable">
          <h5 class="title is-4 p-mt-3" style="text-align: center; margin-top: 20px">
            {{ $t("days-of-week-balance") }}
          </h5>
          <DataTable
            id="daysTable"
            ref="daysTable"
            :value="schedule.balances.day_balance"
            class="p-datatable-sm p-datatable-striped"
          >
            <Column
              v-for="(b, i) in schedule.balances.day_names"
              :header="schedule.balances.day_names[i]"
              :field="schedule.balances.day_names[i]"
              :key="i"
            ></Column>
          </DataTable>
        </div>
        <div v-if="schedule.balances && chooseBalance == 'rest_per_week'" class="p-mt-3" id="venueBalance">
          <h5 class="title is-4 p-mt-3" style="text-align: center; margin-top: 20px">
            {{ $t("rest-teams-per-week") }}
          </h5>
          <table>
            <tr v-for="(b, i) in schedule.balances.week_names" :key="i">
              <td>
                {{ b == "Team" ? "" : b }}
              </td>
              <td class="p-text-left" style="font-weight: 400">
                &nbsp; &nbsp; {{ (schedule.balances.cweek_balance[b] || []).join(", ") }}
              </td>
            </tr>
          </table>
        </div>
        <br />
        <div v-if="eligibleTimeslots.length">
          <p>
            {{
              $t(
                "if-you-do-not-want-to-ignore-any-of-these-conflicts-and-constraints-you-can-create-a-new-timeslot-or-adjust-your-constraints-before-retrying-your-schedule",
              )
            }}
          </p>
        </div>
        <br />
        <DataTable :value="timeslotData" :scrollable="true" scrollHeight="300px" class="p-datatable-sm">
          <Column field="week" :header="$t('week')" :sortable="true" headerStyle="width: 10%"></Column>
          <Column field="date" :header="$t('date')" :sortable="true" headerStyle="width: 10%"></Column>
          <Column field="day" :header="$t('day')" headerStyle="width: 10%"></Column>
          <Column field="time" :header="$t('time')" :sortable="true" headerStyle="width: 10%"></Column>
          <Column field="venue_surface" :header="$t('venue-surface')" :sortable="true" headerStyle="width: 20%"></Column>
          <Column field="timeslot" headerStyle="width: 25%">
            <template #header>
              <div style="display: flex; align-items: center">
                {{ $t('unscheduled-reason') }}
                {{ batchProcessDryRun() }}
              </div>
            </template>
            <template #body="slotProps">
              <div :id="'reason' + '-' + slotProps.data.timeslot.id"> checking... </div>
            </template>
          </Column>
          <Column field="assignment" header="" headerStyle="width: 15%">
            <template #body="slotProps">
              <Button
                :id="'assign' + '-' + slotProps.data.timeslot.id"
                :label="$t('choose')"
                @click="assign(slotProps.data.timeslot)"
              />
            </template>
          </Column>
        </DataTable>
        <div class="p-field p-col-12 p-text-center">
          <br /><br />
          <Button :label="$t('Button.cancel')" @click="chooseDialog = false" class="p-button-lg p-button-outlined" />
        </div>
      </div>
    </Dialog>
  </div>
</template>

<script>
import api from "@/api";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
export default {
  props: {
    schedule: {
      type: Object,
      required: false,
    },
  },
  components: { DataTable, Column },
  // ddddd
  data() {
    return {
      overrideConstraints: false,
      creating: false,
      activeItem: null,
      chooseDialog: null,
      date: null,
      timeslot: {
        start: null,
        finish: null,
        venue_id: null,
        surface_id: null,
      },
      chooseBalance: "team_week_balance",
    };
  },
  computed: {
    eligibleTimeslots() {
      console.log("Check eligible from " + this.schedule.open_timeslots.length);
      var results = [];
      var teams = [this.activeItem.game.home_id, this.activeItem.game.visitor_id];
      var div_id = this.activeItem.game.division_id;
      // console.log(teams)
      this.schedule.open_timeslots.forEach(t => {
        var can = true;
        console.log(t);
        if (t.divisions && t.divisions.length && !t.divisions.includes(div_id)) {
          console.log("Block divisions");
          can = false;
        }
        if (can) {
          var ts = this.$moment(t.start);
          var te = this.$moment(t.finish);
          this.schedule.scheduled_items.forEach(i => {
            if ((can && teams.includes(i.game.home_id)) || teams.includes(i.game.visitor_id)) {
              var cs = this.$moment(i.timeslot.start);
              var ce = this.$moment(i.timeslot.finish);
              if (ts.isBetween(cs, ce, "[]")) {
                console.log("🔴 Starts in middle of slot");
                can = false;
              } else if (te.isBetween(cs, ce, "[]")) {
                console.log("🔴 Ends in middle of slot");
                can = false;
              } else if (ts.isSame(cs)) {
                console.log("🔴 Start Same");
                can = false;
              } else if (te.isSame(ce)) {
                console.log("🔴 End Same");
                can = false;
              }
            }
          });
        }
        if (can) {
          console.log("Can use ✅");
          results.push(t);
        }
      });
      console.log("Results: " + results.length);
      return results.sort(this.dynamicSort("start"));
    },
    valid() {
      return (
        !!this.timeslot.start &&
        !!this.timeslot.finish &&
        !(this.timeslot.finish < this.timeslot.start) &&
        !!this.timeslot.venue_id &&
        !!this.timeslot.surface_id
      );
    },
    seasonItems() {
      if (!this.$store.getters.currentSeason) {
        return {};
      }
      var venues = [];
      var teams = [];
      var surfaces = [];
      var division_id = null;
      if (this.schedule) {
        division_id = this.schedule.division_id;
      }
      this.$store.getters.currentSeason.venues.forEach(v => {
        venues.push({
          name: v.name,
          id: v.id,
          surfaces: v.surfaces,
        });
        if (this.timeslot.venue_id == v.id) {
          v.surfaces.forEach(s => {
            surfaces.push(s);
          });
        }
      });

      this.$store.getters.currentSeason.teams.forEach(t => {
        if (!division_id || t.division_id == division_id) {
          teams.push({
            name: t.name,
            id: t.id,
          });
        }
      });

      var x = {
        venues: venues,
        teams: teams,
        surfaces: surfaces,
        schedules: this.$store.getters.currentSeason.schedules,
      };
      console.log(x);
      return x;
    },
    timeslotData() {
      var tableData = [];
      this.eligibleTimeslots.forEach(t => {
        tableData.push({
          week: this.$moment(this.getWeekStart(t.start)).format("MM-DD-YY"),
          date: this.$moment(t.start).format("MM-DD-YY"),
          day: this.$moment(t.start).format("dddd"),
          time: this.$moment(t.start).format("LT"),
          venue_surface: t.venue.name + " --- " + t.surface.name,
          timeslot: t,
        });
      });
      return tableData;
    },
  },
  methods: {
    checkEnd() {
      if (!this.timeslot.finish) {
        return;
      }
      if (this.timeslot.finish < this.timeslot.start) {
        alert(this.$t("finish-must-be-after-end"));
        return;
      }
    },
    setEnd() {
      if (!this.timeslot.start) {
        return;
      }
      console.log("SetEnd from ", this.timeslot.start);
      this.timeslot.finish = this.$moment(this.timeslot.start)
        .add(2, "hours")
        .format();
      console.log("Set to " + this.timeslot.finish);
    },
    saveTimeslot() {
      this.timeslot.schedule_item_id = this.activeItem.id;
      this.timeslot.season_id = this.currentSeason.id;
      api
        .createTimeslot(this.timeslot)
        .then(response => {
          console.log("Response", response);
          this.makeToast("success", this.$t("saved"));
          this.$emit("refresh");
          this.chooseDialog = false;
        })
        .catch(e => {
          console.log("Error...");
          console.log(e);
          this.makeToast("error", this.$t("could-not-save"));
          this.$emit("refresh");
        });
    },
    getWeekStart(tstart) {
      const fd = this.currentUser.first_day;
      var date = new Date(tstart);
      if (fd === 0) {
        var startOfWeek = new Date(date.setDate(date.getDate() - date.getDay()));
      } else {
        var startOfWeek = new Date(date.setDate(date.getDate() - ((date.getDay() + (7 - fd)) % 7)));
      }
      return startOfWeek;
    },
    dryRunAssignment(timeslot) {
      const item = Object.assign({}, this.activeItem);
      item.timeslot = timeslot;
      item.override = false;
      api
        .dryRunScheduleItem(this.$store.getters.currentSeason.id, item)
        .then(response => {
          if (!response.data.good) {
            var response_msg = response.data.message;
            // if response message is null, then assign - to the reason
            if (response_msg == null) {
              document.getElementById("reason-" + timeslot.id).innerHTML = this.$t("could-not-fetch-reason");
              document.getElementById("assign-" + timeslot.id).children[0].innerHTML = this.$t("ignore-and-assign");
              return;
            }
            if (response_msg.indexOf("---->") == -1) {
              document.getElementById("reason-" + timeslot.id).innerHTML = response_msg;
              document.getElementById("assign-" + timeslot.id).children[0].innerHTML = this.$t("ignore-and-assign");
              return;
            } else {
              var reason = response_msg.slice(response_msg.indexOf("---->") + "---->".length);
              document.getElementById("reason-" + timeslot.id).innerHTML = reason;
              document.getElementById("assign-" + timeslot.id).children[0].innerHTML = this.$t("ignore-and-assign");
              return;
            }
          } else {
            document.getElementById("reason-" + timeslot.id).innerHTML = response.data.message;
            document.getElementById("assign-" + timeslot.id).children[0].innerHTML = this.$t("choose");
          }
        })
        .catch(() => {
          const reason = document.getElementById("reason-" + timeslot.id);
          if (reason) {
            reason.innerHTML = this.$t("conflict-or-issue-detected-please-use-another-timeslot");
          }
        });
    },
    serializeDryRun() {
      this.eligibleTimeslots.forEach(t => {
        this.dryRunAssignment(t);
      });
    },
    batchProcessDryRun() {
      const eligibleTimeslots = this.eligibleTimeslots;
      const batchLimit = 300;
      const delay = 30000; // 30 seconds
      const totalTimeslots = eligibleTimeslots.length;

      // Handle small batch sizes directly
      if (totalTimeslots <= batchLimit) {
        eligibleTimeslots.forEach(timeslot => {
          this.dryRunAssignment(timeslot);
        });
      } else {
        // Process in batches with delay
        for (let i = 0; i < totalTimeslots; i += batchLimit) {
          // Calculate the delay based on the index of the batch
          const batchDelay = (i / batchLimit) * delay;

          setTimeout(() => {
            // Process the current batch
            const batch = eligibleTimeslots.slice(i, i + batchLimit);
            batch.forEach(timeslot => {
              this.dryRunAssignment(timeslot);
            });
          }, batchDelay);
        }
      }
    },
    assign(timeslot, override = false) {
      override = document.getElementById("assign-" + timeslot.id).children[0].innerHTML == this.$t("ignore-and-assign");
      if (
        override
          ? confirm(
              this.$t(
                "are-you-sure-you-want-to-ignore-the-constraints-document-getelementbyid-reason-timeslot-id-innerhtml",
                [document.getElementById("reason-" + timeslot.id).innerHTML],
              ),
            )
          : true
      ) {
        const item = Object.assign({}, this.activeItem);
        item.timeslot = timeslot;
        item.override = override; //override constraints
        console.log(item);
        api
          .updateScheduleItem(this.$store.getters.currentSeason.id, item)
          .then(response => {
            console.log(response);
            console.log("Good: " + response.data.good);
            if (!response.data.good) {
              alert(
                response.data.message +
                  this.$t("please-use-another-timeslot-or-check-the-ignore-constraints-box-to-force-assign"),
              );
              document.getElementById("assign-" + timeslot.id).children[0].innerHTML = this.$t("bad");
              return;
            }

            this.makeToast("success", this.$t("scheduled-game"));
            this.chooseDialog = false;
            this.activeItem = null;
            this.$emit("assigned", response.data);
          })
          .catch(e => {
            console.log("Got error...");
            console.log(e);
            alert(this.$t("conflict-or-issue-detected-please-use-another-timeslot"));
            document.getElementById("assign-" + timeslot.id).children[0].innerHTML = this.$t("bad");

            console.log(e);
          });
      }
    },
    deleteGame(item) {
      var x = window.confirm(this.$t("are-you-sure-this-game-will-be-deleted-and-removed-from-the-unscheduled-list"));
      if (x) {
        api
          .deleteScheduleItems({
            items: [item.id],
          })
          .then(() => {
            this.makeToast("success", this.$t("Toast.delete_game"));
            this.$emit("deleted", item.id);
            this.chooseDialog = false;
          })
          .catch(e => {
            console.log(e);
            this.makeToast("error", this.$t("could-not-delete-game"));
          });
      }
    },
    startChoose(item) {
      console.log("SetActive: ", item);
      this.activeItem = item;
      this.$emit("getBalanceTables");
      this.chooseDialog = true;
    },
  },
  mounted() {
    console.log(this.schedule);
    this.$emit("getBalanceTables");
  },
};
</script>

<style scoped lang="scss"></style>
